/**
 * AtelierOverlay · RISO_FINAL_DESIGN Atelier integration container.
 * Per slot_riso_final_ds Phase A1.6 (15 MAY 2026) · implements ADR-010.
 *
 * Renders Atelier sub-app (separate SPA entry /atelier.html) inside an iframe
 * positioned as an overlay above main-app content. Sidebar layer stays visible
 * top-level — visual continuity per Genie design intent.
 *
 * Mobile path (per Genie pick 15 MAY 2026 · BottomTabs A1.4):
 *   Atelier removed from mobile BottomTabs items. If user reaches `/atelier`
 *   route on mobile via deep-link, overlay renders a friendly banner instead
 *   of the iframe ("Ательер удобнее на десктопе"). Banner has CTA "Понятно"
 *   that closes overlay back to the previous main-app screen.
 *
 * Close paths (per ADR-010 §Implementation notes):
 *   1. ESC key — global listener while overlay is open
 *   2. Sidebar Atelier item re-click — handled by NavShell consumer
 *   3. close-CTA inside Atelier topbar — via `atelier:close` postMessage
 *   4. browser back — handled by NavShell route restoration (A1.5)
 *
 * Focus management:
 *   - On open: focus moves to iframe first interactive (browser-handled)
 *     OR to overlay close button if iframe focus not available
 *   - On close: focus returns to Atelier nav item (consumer responsibility)
 *
 * Main app state preserved — overlay sits on top, doesn't unmount main React tree.
 *
 * Defaults committed (revertible · surfaced to Genie 15 MAY 2026):
 *   - z-index: 40 (between sidebar z=30 and global modals z=50+)
 *   - default-route at open: /atelier root (Atelier shows own landing)
 *   - loading state: subtle "Загружаю Ательер..." caption + paper bg
 *   - mobile variant: full-screen banner with friendly message + close
 */

import { useEffect, useRef, useState } from 'react';

export interface AtelierOverlayProps {
  /** Whether overlay is open. NavShell (A1.5) controls this state. */
  open: boolean;
  /** Close handler. Triggers focus return on consumer side. */
  onClose: () => void;
  /**
   * Initial route inside Atelier sub-app at open time.
   * Default 'root' — shows Atelier landing. Future: 'sprint-N' deep-links.
   */
  initialRoute?: string;
  /**
   * Viewport hint from NavShell. 'mobile' → render friendly banner instead
   * of iframe (Atelier sub-app not adapted for narrow viewports).
   */
  device?: 'desktop' | 'tablet' | 'mobile';
}

const Z_INDEX_OVERLAY = 40;

export function AtelierOverlay({
  open,
  onClose,
  initialRoute = 'root',
  device = 'desktop',
}: AtelierOverlayProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // ESC key closes overlay (per ADR-010 close path 1).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Listen for atelier:close postMessage from Atelier sub-app topbar CTA
  // (per ADR-010 close path 3).
  useEffect(() => {
    if (!open) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data && typeof e.data === 'object' && e.data.type === 'atelier:close') {
        onClose();
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [open, onClose]);

  // Reset loaded state when overlay closes — next open re-shows loading.
  useEffect(() => {
    if (!open) setIframeLoaded(false);
  }, [open]);

  // B.0 · CF-A1-focus-management absorption (16 MAY 2026).
  // On open, programmatically focus the iframe so keyboard navigation enters
  // the Atelier sub-app immediately. Real browsers usually focus the iframe
  // on click but не guaranteed (depends on user gesture vs. keyboard nav);
  // explicit focus() makes a11y predictable + jsdom-testable. Mobile banner
  // path handles its own focus via the «Понятно» button (browser default).
  useEffect(() => {
    if (!open) return;
    if (device === 'mobile') return;
    // Defer to next tick so iframe element is mounted in the DOM.
    const id = window.setTimeout(() => {
      iframeRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, device]);

  if (!open) return null;

  // VIS-5 (полный адаптив · Genie 28 JUN): мобильный баннер-дефлект убран — Артель
  // открывается в iframe на любом устройстве; сам магазин адаптивен до 360px.
  // iframe-isolated Atelier sub-app per ADR-007 + ADR-010.
  // src includes route hint via query · sub-app can pick up and navigate.
  const iframeSrc = initialRoute === 'root' ? '/atelier.html' : `/atelier.html?route=${encodeURIComponent(initialRoute)}`;

  return (
    <div
      className="atelier-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Артель"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        // H.4-EXT4.0 (17 MAY 2026 slot_riso_phase_h_final_smoke · Genie pick c
        // round 4): main sidebar hidden entirely когда overlay open (cleaner
        // sub-app context isolation · supersedes ADR-010 «sidebar visible»).
        // Overlay takes full width · sidebar hide rule в body.atelier-overlay-open
        // selector в globals.css. ESC + Atelier sub-app close button + browser
        // back остаются как exit paths. Was: `left: device === 'tablet' ? 60 : 64`.
        left: 0,
        zIndex: Z_INDEX_OVERLAY,
        background: 'var(--bg, #FBEFD9)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!iframeLoaded && (
        <div
          className="atelier-overlay__loading"
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Fraunces, serif',
            fontSize: 16,
            color: 'var(--ink-soft, #4F5A60)',
            pointerEvents: 'none',
            opacity: 0.7,
          }}
        >
          Загружаю Артель…
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Артель"
        onLoad={() => setIframeLoaded(true)}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          border: 0,
          background: 'transparent',
        }}
      />
    </div>
  );
}

/** Exposed for tests · NavShell uses this to coordinate z-index conflicts. */
export const ATELIER_OVERLAY_Z_INDEX = Z_INDEX_OVERLAY;
